import type { Dayjs, OpUnitType } from 'dayjs';

import { ConstMap } from './ConstMap';
import { getTimezoneDayjs } from './time';

export enum RangeUnit {
  Daily = 1,
  Weekly,
  Monthly,
  Hourly,
}

export const RangeUnitLabelMap = new ConstMap<RangeUnit, string>([
  [RangeUnit.Daily, 'Daily'],
  [RangeUnit.Weekly, 'Weekly'],
  [RangeUnit.Monthly, 'Monthly'],
  [RangeUnit.Hourly, 'Hourly'],
]);

export const rangeUnitMap: Record<RangeUnit, OpUnitType> = {
  1: 'date',
  2: 'week',
  3: 'month',
  4: 'hour',
};

const dailyPreset = (long?: boolean) => [
  {
    value: 1,
    name: long ? '7 days' : '7d',
  },
  {
    value: 2,
    name: long ? '30 days' : '30d',
  },
  {
    value: 3,
    name: long ? '90 days' : '90d',
  },
];

const weeklyPreset = (long?: boolean) => [
  {
    value: 1,
    name: long ? '4 weeks' : '4w',
  },
  {
    value: 2,
    name: long ? '12 weeks' : '12w',
  },
  {
    value: 3,
    name: long ? '24 weeks' : '24w',
  },
];

const monthlyPreset = (long?: boolean) => [
  {
    value: 1,
    name: long ? '3 months' : '3m',
  },
  {
    value: 2,
    name: long ? '6 months' : '6m',
  },
  {
    value: 3,
    name: long ? '12 months' : '12m',
  },
];

const presets = [[], dailyPreset(), weeklyPreset(), monthlyPreset()];

const longPresets = [
  [],
  dailyPreset(true),
  weeklyPreset(true),
  monthlyPreset(true),
];

const getDateRangeByDateT = (
  rangeUnit: RangeUnit,
  presetValue: number,
): [Dayjs, Dayjs] => {
  const today = getTimezoneDayjs().startOf('day').tz();
  const result: [Dayjs, Dayjs] = [today, today];

  if (rangeUnit === RangeUnit.Daily) {
    if (presetValue === 1) {
      result[0] = today.addVal(-6, 'day');
    } else if (presetValue === 2) {
      result[0] = today.addVal(-29, 'day');
    } else if (presetValue === 3) {
      result[0] = today.addVal(-89, 'day');
    } else {
      return result;
    }

    result[1] = today.addVal(1, 'day');
  }

  if (rangeUnit === RangeUnit.Weekly) {
    const startOfWeek = today.startOf('week').tz();
    if (presetValue === 1) {
      result[0] = startOfWeek.addVal(-3, 'week');
    } else if (presetValue === 2) {
      result[0] = startOfWeek.addVal(-11, 'week');
    } else if (presetValue === 3) {
      result[0] = startOfWeek.addVal(-23, 'week');
    } else {
      return result;
    }
    result[1] = today.set('day', 7);
  }

  if (rangeUnit === RangeUnit.Monthly) {
    const startOfMonth = today.startOf('month').tz();
    if (presetValue === 1) {
      result[0] = startOfMonth.addVal(-2, 'month');
    } else if (presetValue === 2) {
      result[0] = startOfMonth.addVal(-5, 'month');
    } else if (presetValue === 3) {
      result[0] = startOfMonth.addVal(-11, 'month');
    } else {
      return result;
    }

    result[1] = startOfMonth.addVal(1, 'month');
  }
  result[0] = result[0].tz();
  result[1] = result[1].tz();
  return result;
};

const getDatePreset = (
  rangeUnit: RangeUnit,
  long?: boolean,
): CommonListItemT<number, string>[] => {
  if (long) return longPresets[rangeUnit];
  return presets[rangeUnit];
};

export { getDateRangeByDateT };

export default getDatePreset;
